import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  Tooltip,
  ReferenceLine,
  Cell,
} from 'recharts';
import type { DietLog } from '../../../types';

interface MacroTargets {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
}

interface MacroLogged {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
}

interface MacroDashboardProps {
  targets: MacroTargets;
  logged: MacroLogged;
  weeklyLogs: DietLog[];
}

function MacroRing({
  label,
  logged,
  target,
  color,
}: {
  label: string;
  logged: number;
  target: number;
  color: string;
}) {
  const pct = target > 0 ? Math.min((logged / target) * 100, 100) : 0;
  const r = 32;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (pct / 100) * circumference;

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative w-20 h-20">
        <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
          <circle cx="40" cy="40" r={r} fill="none" stroke="#f3f4f6" strokeWidth="8" />
          <circle
            cx="40"
            cy="40"
            r={r}
            fill="none"
            stroke={color}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            style={{ transition: 'stroke-dashoffset 0.5s ease' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-sm font-bold text-gray-800">{Math.round(logged)}g</span>
        </div>
      </div>
      <p className="text-xs font-medium text-gray-600">{label}</p>
      <p className="text-xs text-gray-400">{target}g target</p>
    </div>
  );
}

function getWeeklyChartData(logs: DietLog[], calorieTarget: number) {
  const days: { day: string; date: string }[] = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    days.push({
      day: d.toLocaleDateString(undefined, { weekday: 'short' }),
      date: d.toLocaleDateString('en-CA'),
    });
  }
  return days.map(({ day, date }) => {
    const log = logs.find(l => l.date === date);
    const calories = log?.totalCalories ?? 0;
    return { day, calories, target: calorieTarget };
  });
}

export default function MacroDashboard({ targets, logged, weeklyLogs }: MacroDashboardProps) {
  const caloriePct = targets.calories > 0 ? Math.min((logged.calories / targets.calories) * 100, 110) : 0;
  const calorieColor = caloriePct >= 110 ? '#ef4444' : caloriePct >= 90 ? '#f59e0b' : '#22c55e';

  const weeklyData = getWeeklyChartData(weeklyLogs, targets.calories);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-5">
      {/* Calorie bar */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-sm font-semibold text-gray-700">Calories</span>
          <span className="text-sm font-bold" style={{ color: calorieColor }}>
            {Math.round(logged.calories)} / {targets.calories} kcal
          </span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
          <div
            className="h-3 rounded-full transition-all duration-500"
            style={{
              width: `${Math.min(caloriePct, 100)}%`,
              backgroundColor: calorieColor,
            }}
          />
        </div>
        <p className="text-xs text-gray-400 mt-1">
          {targets.calories - logged.calories > 0
            ? `${Math.round(targets.calories - logged.calories)} kcal remaining`
            : `${Math.round(logged.calories - targets.calories)} kcal over target`}
        </p>
      </div>

      {/* Macro rings */}
      <div className="flex justify-around mb-6">
        <MacroRing key="protein" label="Protein" logged={logged.protein} target={targets.protein} color="#3b82f6" />
        <MacroRing key="carbs" label="Carbs" logged={logged.carbs} target={targets.carbs} color="#f59e0b" />
        <MacroRing key="fat" label="Fat" logged={logged.fat} target={targets.fat} color="#ef4444" />
      </div>

      {/* 7-day trend */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">7-Day Calories</p>
        <ResponsiveContainer width="100%" height={100}>
          <BarChart data={weeklyData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
            <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
            <Tooltip
              formatter={(v: any) => [`${v} kcal`, 'Calories']}
              contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
            />
            {targets.calories > 0 && (
              <ReferenceLine y={targets.calories} stroke="#6366f1" strokeDasharray="4 4" strokeWidth={1} />
            )}
            <Bar dataKey="calories" radius={[4, 4, 0, 0]} maxBarSize={28}>
              {weeklyData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.calories > entry.target && entry.target > 0 ? '#fca5a5' : '#86efac'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
